import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircleOutlined as ApproveIcon,
  DeleteOutlined as DeleteIcon,
  PauseCircleOutlined as DisableIcon,
  PlayCircleOutlined as EnableIcon,
  AddOutlined as AddIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { agentLessonsApi, GlobalAgentLesson, GlobalAgentLessonCreate } from '@/services/api';

const CATEGORIES = ['general', 'git', 'testing', 'coding', 'planning', 'communication'];

const CATEGORY_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'> = {
  git: 'warning',
  testing: 'success',
  coding: 'primary',
  planning: 'info',
  communication: 'secondary',
  general: 'default',
};

function LessonRow({
  lesson,
  onApprove,
  onEnable,
  onDisable,
  onDelete,
}: {
  lesson: GlobalAgentLesson;
  onApprove: (id: string) => void;
  onEnable: (id: string) => void;
  onDisable: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const sourceLink =
    lesson.source_team_id && lesson.source_task_id ? (
      <Typography
        component="a"
        href={`/teams/${lesson.source_team_id}/agent`}
        target="_blank"
        rel="noopener noreferrer"
        variant="caption"
        sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
      >
        {lesson.source_task_id.slice(0, 8)}
      </Typography>
    ) : null;

  return (
    <Box sx={{ py: 1.5, px: 1 }}>
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={0.5}>
            <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 400 }}>
              {lesson.title}
            </Typography>
            <Chip
              label={lesson.category}
              size="small"
              color={CATEGORY_COLORS[lesson.category] ?? 'default'}
              variant="outlined"
            />
            {sourceLink && (
              <Typography variant="caption" color="text.secondary">
                from task {sourceLink}
              </Typography>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {lesson.body.length > 150 ? lesson.body.slice(0, 150) + '…' : lesson.body}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5} flexShrink={0}>
          {!lesson.approved && (
            <Tooltip title="Approve">
              <IconButton size="small" color="success" onClick={() => onApprove(lesson.id)}>
                <ApproveIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {lesson.approved && !lesson.is_active && (
            <Tooltip title="Enable">
              <IconButton size="small" color="primary" onClick={() => onEnable(lesson.id)}>
                <EnableIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {lesson.approved && lesson.is_active && (
            <Tooltip title="Disable">
              <IconButton size="small" color="warning" onClick={() => onDisable(lesson.id)}>
                <DisableIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => onDelete(lesson.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Box>
  );
}

export default function AgentLessonsPanel() {
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [newLesson, setNewLesson] = useState<GlobalAgentLessonCreate>({
    title: '',
    body: '',
    category: 'general',
    approved: false,
    is_active: false,
  });

  const { data: allLessons = [], isLoading } = useQuery({
    queryKey: ['admin', 'agent-lessons'],
    queryFn: () => agentLessonsApi.list().then((r) => r.data),
  });

  const pendingLessons = allLessons.filter((l) => !l.approved);
  const activeLessons = allLessons.filter((l) => l.approved && l.is_active);
  const disabledLessons = allLessons.filter((l) => l.approved && !l.is_active);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'agent-lessons'] });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof agentLessonsApi.update>[1] }) =>
      agentLessonsApi.update(id, data),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => agentLessonsApi.delete(id),
    onSuccess: invalidate,
  });

  const createMutation = useMutation({
    mutationFn: (data: GlobalAgentLessonCreate) => agentLessonsApi.create(data),
    onSuccess: () => {
      invalidate();
      setAddOpen(false);
      setNewLesson({ title: '', body: '', category: 'general', approved: false, is_active: false });
    },
  });

  const handleApprove = (id: string) =>
    updateMutation.mutate({ id, data: { approved: true } });
  const handleEnable = (id: string) =>
    updateMutation.mutate({ id, data: { is_active: true } });
  const handleDisable = (id: string) =>
    updateMutation.mutate({ id, data: { is_active: false } });
  const handleDelete = (id: string) => deleteMutation.mutate(id);

  const Section = ({
    title,
    lessons,
    emptyMsg,
  }: {
    title: string;
    lessons: GlobalAgentLesson[];
    emptyMsg: string;
  }) => (
    <Box mb={3}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {title} ({lessons.length})
      </Typography>
      <Divider sx={{ mb: 1 }} />
      {lessons.length === 0 ? (
        <Typography variant="body2" color="text.disabled" sx={{ py: 1 }}>
          {emptyMsg}
        </Typography>
      ) : (
        lessons.map((l, i) => (
          <Box key={l.id}>
            <LessonRow
              lesson={l}
              onApprove={handleApprove}
              onEnable={handleEnable}
              onDisable={handleDisable}
              onDelete={handleDelete}
            />
            {i < lessons.length - 1 && <Divider />}
          </Box>
        ))
      )}
    </Box>
  );

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h6">Agent Lessons</Typography>
          <Typography variant="body2" color="text.secondary">
            {activeLessons.length} / 50 active lessons
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setAddOpen(true)}
        >
          Add Lesson
        </Button>
      </Stack>

      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      <Section
        title="Pending Review"
        lessons={pendingLessons}
        emptyMsg="No draft lessons awaiting review."
      />
      <Section
        title="Active"
        lessons={activeLessons}
        emptyMsg="No active lessons. Approve and enable a draft to start injecting lessons."
      />
      <Section
        title="Disabled"
        lessons={disabledLessons}
        emptyMsg="No disabled lessons."
      />

      {/* Add lesson dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Lesson</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Title"
              fullWidth
              size="small"
              value={newLesson.title}
              onChange={(e) => setNewLesson((p) => ({ ...p, title: e.target.value }))}
              inputProps={{ maxLength: 500 }}
            />
            <TextField
              label="Body"
              fullWidth
              multiline
              minRows={3}
              size="small"
              value={newLesson.body}
              onChange={(e) => setNewLesson((p) => ({ ...p, body: e.target.value }))}
            />
            <Select
              size="small"
              value={newLesson.category}
              onChange={(e) => setNewLesson((p) => ({ ...p, category: e.target.value }))}
            >
              {CATEGORIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!newLesson.title.trim() || !newLesson.body.trim() || createMutation.isPending}
            onClick={() => createMutation.mutate(newLesson)}
          >
            Create Draft
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
